<template>
  <div class="tests-page">
    <div class="page-header">
      <h1><i class="pi pi-file"></i> Доступні тести</h1>
      <p>Переглядайте та проходьте доступні тестування</p>
    </div>

    <div class="content-section">
      <div class="filters">
        <div class="filter-group">
          <label>Статус:</label>
          <Dropdown
            v-model="selectedStatus"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Всі статуси"
            class="filter-dropdown"
          />
        </div>
        <div class="filter-group">
          <label>Тип тесту:</label>
          <Dropdown
            v-model="selectedTestType"
            :options="testTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Всі типи"
            class="filter-dropdown"
          />
        </div>
      </div>

      <div class="sessions-grid">
        <div
          v-for="session in filteredSessions"
          :key="session.id"
          class="session-card"
          :class="{ 'expired': session.status === 'expired' }"
        >
          <div class="session-header">
            <div class="session-name-container">
              <h3>{{ session.name }}</h3>
              <Tag 
                v-if="session.is_anonymous" 
                value="Анонімні відповіді" 
                severity="info" 
                class="anonymous-badge"
              />
            </div>
            <Tag
              :value="getStatusLabel(session.status)"
              :severity="getStatusSeverity(session.status)"
            />
          </div>
          
          <div class="session-info">
            <p class="description">{{ session.description }}</p>
            <div class="meta-info">
              <div class="meta-item">
                <i class="pi pi-calendar"></i>
                <span>Початок: {{ formatDate(session.start_date) }}</span>
              </div>
              <div class="meta-item">
                <i class="pi pi-clock"></i>
                <span>Кінець: {{ formatDate(session.end_date) }}</span>
              </div>
              <div class="meta-item">
                <i class="pi pi-users"></i>
                <span>Учасників: {{ session.participants?.length || 0 }}</span>
              </div>
            </div>
          </div>

          <div class="session-actions">
            <Button
              v-if="session.status === 'active' && canTakeTest(session)"
              label="Почати тест"
              icon="pi pi-play"
              class="p-button-success"
              @click="startTest(session.id)"
            />
            <Button
              v-else-if="hasCompletedTest(session)"
              label="Переглянути результат"
              icon="pi pi-eye"
              class="p-button-info"
              @click="viewResult(session.id)"
            />
            <Button
              v-else
              :label="getActionLabel(session.status)"
              :icon="getActionIcon(session.status)"
              :class="getActionClass(session.status)"
              disabled
            />
          </div>
        </div>
      </div>

      <div v-if="filteredSessions.length === 0" class="empty-state">
        <i class="pi pi-inbox empty-icon"></i>
        <h3>Немає доступних тестів</h3>
        <p>Наразі немає активних тестових сесій для проходження.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'Tests',
  data() {
    return {
      selectedStatus: null,
      selectedTestType: null,
      statusOptions: [
        { label: 'Всі статуси', value: null },
        { label: 'Активні', value: 'active' },
        { label: 'Завершені', value: 'completed' },
        { label: 'Завершені', value: 'expired' }
      ],
      testTypeOptions: [
        { label: 'Всі типи', value: null },
        { label: 'Gallup Q12', value: 'gallup_q12' },
        { label: 'eNPS', value: 'enps' },
        { label: 'Кастомні', value: 'custom' }
      ]
    }
  },
  computed: {
    ...mapState(['sessions', 'results']),
    filteredSessions() {
      let filtered = this.sessions

      if (this.selectedStatus) {
        filtered = filtered.filter(session => session.status === this.selectedStatus)
      }

      if (this.selectedTestType) {
        filtered = filtered.filter(session => session.test_type?.category === this.selectedTestType)
      }

      return filtered
    }
  },
  methods: {
    ...mapActions(['fetchSessions']),
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('uk-UA')
    },
    getStatusLabel(status) {
      const labels = {
        active: 'Активна',
        completed: 'Завершена',
        expired: 'Завершена',
        cancelled: 'Скасована'
      }
      return labels[status] || status
    },
    getStatusSeverity(status) {
      const severities = {
        active: 'success',
        completed: 'info',
        expired: 'warning',
        cancelled: 'danger'
      }
      return severities[status] || 'info'
    },
    canTakeTest(session) {
      const participant = session.participants?.find(p => p.user === this.currentUser?.id)
      return participant && participant.status === 'invited'
    },
    hasCompletedTest(session) {
      return this.results.some(result => result.session === session.id)
    },
    getActionLabel(status) {
      const labels = {
        active: 'Почати тест',
        completed: 'Завершено',
        expired: 'Завершено',
        cancelled: 'Скасовано'
      }
      return labels[status] || 'Недоступно'
    },
    getActionIcon(status) {
      const icons = {
        active: 'pi pi-play',
        completed: 'pi pi-check',
        expired: 'pi pi-clock',
        cancelled: 'pi pi-times'
      }
      return icons[status] || 'pi pi-ban'
    },
    getActionClass(status) {
      const classes = {
        active: 'p-button-success',
        completed: 'p-button-info',
        expired: 'p-button-warning',
        cancelled: 'p-button-danger'
      }
      return classes[status] || 'p-button-secondary'
    },
    startTest(sessionId) {
      this.$router.push(`/test/${sessionId}`)
    },
    viewResult(sessionId) {
      this.$router.push(`/results?session=${sessionId}`)
    }
  },
  async mounted() {
    await this.fetchSessions()
  }
}
</script>

<style scoped>
.tests-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--text-color-secondary);
  margin: 0;
}

.content-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
}

.filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color);
}

.filter-dropdown {
  min-width: 150px;
}

.sessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.session-card {
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.session-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.session-card.expired {
  opacity: 0.7;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.session-header h3 {
  margin: 0;
  color: var(--text-color);
  flex: 1;
}

.session-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
}

.anonymous-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.session-info {
  margin-bottom: 1.5rem;
}

.description {
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
  line-height: 1.5;
}

.meta-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.meta-item i {
  color: var(--primary-color);
}

.session-actions {
  display: flex;
  gap: 0.5rem;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: var(--text-color-secondary);
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

@media (max-width: 768px) {
  .tests-page {
    padding: 1rem;
  }
  
  .sessions-grid {
    grid-template-columns: 1fr;
  }
  
  .filters {
    flex-direction: column;
  }
}
</style> 